import axios from 'axios';
import { Config } from '../../Common/Config';

// Recursively encode a value into PHP/Laravel bracket-notation query params
// (`key[sub]=v`, `key[0][field]=v`, ...) — the format `$request->input()`
// decodes back into a nested array server-side. Appends "key=value" strings
// (already URI-encoded) to `out`.
function appendBracketParam(out, key, value) {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((v, i) => appendBracketParam(out, `${key}[${i}]`, v));
  } else if (typeof value === 'object') {
    Object.entries(value).forEach(([k, v]) => appendBracketParam(out, `${key}[${k}]`, v));
  } else {
    out.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
}

// ── Analytics client (booking-analytics/* endpoints) ──────────────────────────
const getApiClient = () =>
  axios.create({
    baseURL: `${Config.url}booking-analytics`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

// ── Trace-log client — matches prod URL: /api/admin/v1/trace-logs/page/{page} ─
// Prod pattern (from api.js on prod branch):
//   let url = `${API_BASE_URL}trace-logs/page/${page}`;
// API_BASE_URL = Config.url = https://api.ldcbe.webdezign.uk/api/admin/v1/
const getTraceApiClient = () =>
  axios.create({
    baseURL: Config.url,                        // https://.../api/admin/v1/
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

export const BookingAnalyticsApi = {
  /**
   * GET /booking-analytics/analytics
   * Fetch aggregated dashboard data (charts, funnels, conversion rates).
   * @param {{ date_from: string, date_to: string }} params
   */
  getDashboard: (params) =>
    getApiClient()
      .get('/analytics', { params })
      .then((r) => r.data),

  /**
   * GET /trace-logs/page/{page}?status=&module=&search=&start_date=&end_date=&per_page=
   * Paginated & filtered trace log list — matches prod api.js URL pattern.
   * Response: { success, data: { data: [...], meta: { total, per_page, current_page, last_page }, summary: { total, confirmed, expired, abandoned, failed, pending } } }
   * @param {{ page?: number, per_page?: number, status?: string, module?: string, search?: string, start_date?: string, end_date?: string }} params
   */
  getLogs: ({ page = 1, ...filters } = {}) => {
    const params = new URLSearchParams();
    if (filters.per_page)  params.append('per_page',   filters.per_page);
    if (filters.status && filters.status !== 'all')   params.append('status',     filters.status);
    if (filters.module && filters.module !== 'all')   params.append('module',     filters.module);
    if (filters.search)    params.append('search',     filters.search);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date)   params.append('end_date',   filters.end_date);
    // date_from / date_to aliases (analytics tab uses these names)
    if (filters.date_from)  params.append('start_date', filters.date_from);
    if (filters.date_to)    params.append('end_date',   filters.date_to);

    const qs = params.toString();
    const url = `trace-logs/page/${page}${qs ? `?${qs}` : ''}`;
    return getTraceApiClient()
      .get(url)
      .then((r) => r.data);
  },

  /**
   * GET /booking-analytics/trace-logs/{identifier}
   * Single trace log detail — the only endpoint that returns decrypted
   * `steps` (requirements/API055 "Single trace-log detail endpoint"; the
   * list endpoints never include `steps`, for performance reasons).
   * `identifier` may be a numeric trace-log `id` or a `session_id` — the
   * backend disambiguates via ctype_digit(), case-insensitive either way.
   * @param {string|number} identifier
   */
  getLogDetail: (identifier) =>
    getTraceApiClient()
      .get(`booking-analytics/trace-logs/${identifier}`)
      .then((r) => r.data),

  /**
   * GET /booking-analytics/trace-logs?page=&per_page=&date_from=&date_to=&filters[...]=
   * Structured AND/OR condition-tree search — see requirements/API055 (ioo-338).
   * `filters` is mutually exclusive with status/module/session_ids/search, but
   * date_from/date_to may be sent alongside it (IMPR002/PLAN095).
   *
   * `filters` is sent as bracket-notation query params (`filters[operator]=...`,
   * `filters[conditions][0][field]=...`), NOT a GET request body — per the
   * WHATWG XHR/fetch spec, browsers silently drop the body on a GET request,
   * so `$request->input('filters')` never sees it that way even though the
   * doc's "JSON body on GET" pattern works fine from non-browser HTTP clients.
   * @param {{ page?: number, per_page?: number, date_from?: string, date_to?: string, filters?: object }} params
   */
  getLogsAdvanced: ({ page = 1, per_page = 20, date_from, date_to, filters } = {}) => {
    const parts = [];
    appendBracketParam(parts, 'page', page);
    appendBracketParam(parts, 'per_page', per_page);
    if (date_from) appendBracketParam(parts, 'date_from', date_from);
    if (date_to) appendBracketParam(parts, 'date_to', date_to);
    if (filters) appendBracketParam(parts, 'filters', filters);

    const url = `booking-analytics/trace-logs${parts.length ? `?${parts.join('&')}` : ''}`;
    return getTraceApiClient()
      .get(url)
      .then((r) => r.data);
  },

  /**
   * POST /booking-analytics/export
   * Trigger CSV / JSON export as a binary blob download.
   * @param {{ type: string, date_from: string, date_to: string }} data
   */
  export: (data) =>
    getApiClient().post('/export', data, { responseType: 'blob' }),

  /**
   * POST /booking-analytics/sync
   * Trigger a manual background data sync.
   * @param {{ force?: boolean, from?: string, to?: string }} [data]
   */
  sync: (data = {}) =>
    getApiClient().post('/sync', data).then((r) => r.data),

  /**
   * GET /booking-analytics/sync/status
   * Check background sync status.
   */
  getSyncStatus: () =>
    getApiClient().get('/sync/status').then((r) => r.data),
};

// Backwards-compat alias — analyticsApi.js calls BookingAnalyticsApi.triggerSync()
BookingAnalyticsApi.triggerSync = BookingAnalyticsApi.sync;

